import Service from '@ember/service';
import { service } from '@ember/service';
import {
  task,
  timeout,
  waitForEvent,
} from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import tippy, { hideAll } from 'tippy.js';
import normalizeIdentifier from 'ember-stereo/-private/utils/normalize-identifier';
import { registerDestructor } from '@ember/destroyable';
import { SyncChannel } from '../utils/sync-channel';

export default class PlaybackService extends Service {
  @service stereo;
  @service feeds;
  @service store;
  @service session;

  @tracked _closed = false;
  @tracked blockedSound;
  @tracked blockedPrompt;

  get playerIsActive() {
    if (this._closed) return false;
    return !!(this.stereo.currentSound || this.blockedSound);
  }

  _syncChannel = null;

  constructor() {
    super(...arguments);
    this._syncChannel = new SyncChannel({ stereo: this.stereo });
    this.addStereoMonitors();
    registerDestructor(this, this.teardown.bind(this));
  }

  // ---------------------------------------------------------------------------
  // Cross-tab sync — proxied from SyncChannel
  // ---------------------------------------------------------------------------

  get remoteIsPlaying()  { return this._syncChannel.remoteIsPlaying; }
  get remoteUrl()        { return this._syncChannel.remoteUrl; }
  get remoteTabId()      { return this._syncChannel.remoteTabId; }
  get remoteWindowName() { return this._syncChannel.remoteWindowName; }
  get isPlayingRemotely() { return this._syncChannel.isPlayingRemotely; }
  get isPlayingLocally()  { return this._syncChannel.isPlayingLocally; }
  get isPlayingAnywhere() { return this._syncChannel.isPlayingAnywhere; }

  /**
   * Send a command to other tabs.
   * @param {'pause'|'play'|'toggle'} command
   * @param {string} [targetTabId] - Pass this.remoteTabId to target the tab that last played.
   */
  sendCommand(command, targetTabId = null) {
    this._syncChannel.sendCommand(command, targetTabId);
  }

  // ---------------------------------------------------------------------------
  // Setup
  // ---------------------------------------------------------------------------

  addStereoMonitors() {
    this._onAudioPaused = this.onAudioPaused.bind(this);
    this._onAudioPlayed = this.onAudioPlayed.bind(this);
    this._onAudioBlocked = this.onAudioBlocked.bind(this);

    this.stereo.on('audio-played', this._onAudioPlayed);
    this.stereo.on('audio-paused', this._onAudioPaused);
    this.stereo.on('audio-ended', this._onAudioPaused);
    this.stereo.on('audio-blocked', this._onAudioBlocked);
  }

  // ---------------------------------------------------------------------------
  // Stereo event handlers
  // ---------------------------------------------------------------------------

  onAudioPlayed() {
    this._closed = false;
    this._syncChannel.broadcastState({
      isPlaying: true,
      url: this.stereo.currentSound?.identifier,
      windowName: window.name || null,
    });
  }

  onAudioPaused() {
    this._syncChannel.broadcastState({
      isPlaying: false,
      url: this.stereo.currentSound?.identifier,
      windowName: window.name || null,
    });
  }

  onAudioBlocked({ sound }) {
    this.blockedSound = sound;
    this.handleAudioBlocked.perform(sound);

    this.stereo.once('audio-played', () => {
      this.blockedSound = null;
      if (this.blockedPrompt) {
        hideAll();
        this.blockedPrompt = null;
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Teardown
  // ---------------------------------------------------------------------------

  teardown() {
    if (!this.stereo.isDestroyed) {
      try {
        this.stereo.off('audio-played', this._onAudioPlayed);
        this.stereo.off('audio-blocked', this._onAudioBlocked);
        this.stereo.off('audio-paused', this._onAudioPaused);
        this.stereo.off('audio-ended', this._onAudioPaused);
      } catch (e) {
        // no op
      }
    }
    this._syncChannel.destroy();
  }

  // ---------------------------------------------------------------------------
  // Playback actions
  // ---------------------------------------------------------------------------

  stopAndClose() {
    this.stereo.stop();
    this._closed = true;
  }

  handleAudioBlocked = task({ restartable: true }, async sound => {
    let query = `[data-identifier="${normalizeIdentifier(
      sound.url,
    )}"] .dubbletrack-player-controls`;
    var elements;
    var waitLength = 0;
    var pauseSize = 200;
    while (true) {
      elements = document.querySelectorAll(query);
      waitLength = waitLength + pauseSize;

      await timeout(pauseSize);
      if ((elements && elements.length > 0) || waitLength > 3000) {
        break;
      }
    }

    let instances = tippy(elements, {
      role: 'tooltip',
      theme: 'attention',
      placement: 'bottom',
      maxWidth: '200px',
      animation: 'shift-away-subtle',
      hideOnClick: true,
      interactive: false,
      content:
        'Your browser blocked our autoplay attempt, so give this button a click to listen',
      trigger: 'manual',
      allowHTML: true,
    });

    this.blockedPrompt = instances[0];
    this.blockedPrompt?.show();
    await timeout(1000);
  });

  waitForNextPlayTask = task({ restartable: true }, async sound => {
    let loadedAirings = this.store.peekAll('airing');
    let time = sound.metadata.endsAt;
    let nextAiring = _.sortBy(
      loadedAirings.filter((a) => {
        return (
          a.endsAt > time &&
          a.endsAt.getTime() - time.getTime() < 1000 * 60 &&
          !a.isOverridden
        );
      }),
      ['startsAt'],
    )[0];

    if (!nextAiring) {
      nextAiring = await this.store.queryRecord('airing', {
        filter: {
          ends_at: {
            gt: time.toISOString(),
          },
          active: true,
        },
        include: ['show', 'broadcast'],
        sort: 'starts_at',
        page: { size: 1, number: 1 },
      });
    }

    await waitForEvent(sound, 'audio-ended');

    if (nextAiring) {
      this.playAiringTask.perform(nextAiring);
    }
  });
}
